/**
 * Short codes for member types, shown next to member ids: "(1555 - A)".
 *
 * The stored `member_type` is not clean. Existing code already compares against
 * both "Vilande spelrätt" and "Vilande spelrätt..", the backend enum uses
 * VILANDE_SPELRATT, and the edit-user form runs values through
 * capFirstAndRemoveDash — so dashes, underscores, casing, trailing dots and
 * missing diacritics all occur. Rather than list every spelling, values are
 * normalised to a comparable form first.
 */

/** "Vilande spelrätt.." / "VILANDE_SPELRATT" / "vilande-spelrätt" -> "vilande spelratt" */
function normalise(value: string): string {
  return value
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .replace(/[-_]+/g, " ")
    .replace(/[^a-z0-9+ ]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const CODES: Record<string, string> = {
  "aktiv spelratt": "A",
  "temporar spelratt": "T",
  "senior spelratt": "S",
  "junior b": "JB",
  junior: "J",
  "junior spelratt": "JS",
  juniorspelratt: "JS",
  "vilande spelratt": "V",
  // Same right, just billed differently.
  "vilande spelratt rabatt": "V",
  "fore detta medlem": "FDM",
  "ex medlem": "FDM",
  gym: "Gym+",
  "gym+": "Gym+",
  "non residential": "NR",
  "prioriterad gast": "PG",
  "prio guest": "PG",
  elitnal: "E",
  hedersledamot: "H",
  foretag: "F",
  "foretag b": "FB",
  personal: "P",
  "utan rattigheter": "MUR",
};

/**
 * The short code for a member type, or undefined when the type isn't one we
 * have a code for. Undefined deliberately renders as a plain id rather than a
 * guess — a wrong code is worse than none.
 */
export function memberTypeCode(memberType?: string): string | undefined {
  if (!memberType) return undefined;
  return CODES[normalise(memberType)];
}

/** "1555" + "Vilande spelrätt" -> "1555 - V"; falls back to just the id. */
export function memberIdLabel(
  memberId?: string,
  memberType?: string,
): string {
  if (!memberId) return "";
  const code = memberTypeCode(memberType);
  return code ? `${memberId} - ${code}` : memberId;
}

/**
 * Träningsgrupp applies to the two junior memberships only - "Junior spelrätt"
 * is a separate membership and is deliberately not included. Compared through
 * the same normalisation as the codes above, since the stored value arrives as
 * "Junior B" from the permission list and as "JUNIOR_B" from the create form.
 */
const TRAINING_GROUP_TYPES = new Set(["junior", "junior b"]);

/** The groups the club divides its juniors into. */
export const TRAINING_GROUPS = [
  "Grundskola",
  "Tennisskola",
  "Tävlingsgrupp",
  "Academy of KLTK",
  "Annat",
];

/** Whether a member of this type is placed in a training group. */
export function hasTrainingGroup(memberType?: string): boolean {
  if (!memberType) return false;
  return TRAINING_GROUP_TYPES.has(normalise(memberType));
}

/**
 * The membership a member waits in until their start date. Set only by the
 * backend - never offered in a membership picker - and only admins see it.
 */
export const INACTIVE_MEMBER_TYPE = "Inaktiverad";

const JUNIOR_MEMBER_TYPES = ["Junior", "Junior B"];

/**
 * The one membership that keeps its normal rules on a drop-in: it can only be
 * booked on its own times, and pays as a co-player outside them.
 */
export const SENIOR_PLAYING_RIGHT = "Senior spelrätt";

export function isJuniorType(memberType?: string): boolean {
  return !!memberType && JUNIOR_MEMBER_TYPES.includes(memberType);
}

/**
 * On a drop-in booked for a junior, a Junior or Junior B co-player plays free,
 * and so does anyone once the time has started; everyone else pays as in an
 * ordinary booking. Mirrors free_on_junior_drop_in in the bookings lambda so
 * the price shown is the price charged.
 */
export function freeOnJuniorDropIn(
  coPlayerType: string | undefined,
  datetimeStart: string
): boolean {
  if (coPlayerType === SENIOR_PLAYING_RIGHT) return false;
  if (isJuniorType(coPlayerType)) return true;
  return new Date() >= new Date(datetimeStart);
}

/** Drop-in opens this many minutes before the start. */
const DROP_IN_MINUTES = 5;

/** Junior spelrätt may only drop in once the time has started. */
const DROP_IN_FROM_START = ["Junior spelrätt", "Junior spelrätt "];

/**
 * Non-residential and Prioriterad Gäst only have drop-in during the periods
 * hard-coded in the bookings lambda's pricing (get_non_residential_price,
 * get_priority_guest_price); outside them they have none.
 */
function inDropInPeriod(
  memberType: string | undefined,
  date: Date,
  courtType: string | undefined
): boolean {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  if (memberType === "Non residential") {
    return (
      (month >= 5 && month <= 8) ||
      (month === 12 && day >= 15) ||
      (month === 1 && day <= 15)
    );
  }
  if (memberType === "Prioriterad Gäst") {
    const inPeriod =
      (month >= 5 && month <= 8) || (month === 9 && day <= 15);
    return courtType === "INDOOR" && inPeriod;
  }
  return true;
}

/**
 * Whether a booking made now for this membership is a drop-in (KLTK,
 * 2026-10-13): from five minutes before the start, or from the start for
 * Junior spelrätt, and for Non-residential and Prioriterad Gäst only within
 * their periods. Reception's bookings switch drop-in on by this.
 */
export function inDropInWindow(
  memberType: string | undefined,
  datetimeStart: string,
  courtType?: string
): boolean {
  const start = new Date(datetimeStart);
  if (!inDropInPeriod(memberType, start, courtType)) return false;
  const opensBefore = DROP_IN_FROM_START.includes(memberType ?? "")
    ? 0
    : DROP_IN_MINUTES;
  return Date.now() >= start.getTime() - opensBefore * 60 * 1000;
}
